import { getFormatterByValue } from './formatter';
import { minorUnitForCurrencyCode, shiftValueByMinorUnit } from './minorUnit';
import type { Options, Value } from './types';

/**
 * Formats a number as a string representing an amount of the specified currency
 *
 * @example
 * ```ts
 * format(123.45, { currency: 'AUD', locale: 'en-AU' }); // '$123.45'
 * ```
 *
 * @returns The currency formatted to a string
 */
export const format = (value: Value, options: Options): string =>
  getFormatterByValue(value, options).format(value);

/**
 * Formats a number as a string representing an amount of the specified currency.
 * The number is interpreted as an amount of minor currency units.
 *
 * @example
 * ```ts
 * formatMinorUnits(12345, { currency: 'AUD', locale: 'en-AU' }); // '$123.45'
 * ```
 *
 * @returns The currency formatted to a string
 */
export const formatMinorUnits = (value: Value, options: Options): string => {
  const minorUnit = minorUnitForCurrencyCode(options.currency);
  const currencyValue = shiftValueByMinorUnit(value, minorUnit);

  return getFormatterByValue(currencyValue, options).format(currencyValue);
};

/**
 * Formats a number to parts.
 * Useful when displaying some parts differently to others.
 *
 * @example
 * ```ts
 * formatToParts(123.45, { currency: 'AUD', locale: 'en-AU' });
 * ```
 *
 * @returns The currency formatted as an array of parts
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/formatToParts#return_value
 */
export const formatToParts = (
  value: Value,
  options: Options,
): Intl.NumberFormatPart[] =>
  getFormatterByValue(value, options).formatToParts(value);

/**
 * Formats a number to parts.
 * Useful for manipulating when displaying some parts differently to others.
 *
 * The number is interpreted as an amount of minor currency units.
 *
 * @example
 * ```ts
 * formatMinorUnitsToParts(123.45, { currency: 'AUD', locale: 'en-AU' });
 * ```
 *
 * @returns The currency formatted as an array of parts
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/formatToParts#return_value
 */
export const formatMinorUnitsToParts = (
  value: Value,
  options: Options,
): Intl.NumberFormatPart[] => {
  const minorUnit = minorUnitForCurrencyCode(options.currency);
  const currencyValue = shiftValueByMinorUnit(value, minorUnit);

  return getFormatterByValue(currencyValue, options).formatToParts(
    currencyValue,
  );
};
